import { useNavigate } from 'react-router';
import { useList } from '@refinedev/core';
import { DataTable, type Column } from '../../../components/DataTable.js';
import { humanizeEnum } from '../../../components/Form.js';

interface MaintenanceRequestRow {
  id: string;
  title: string;
  status: string;
  unitLabel: string;
  propertyName: string;
  createdAt: string | null;
}

/**
 * A tenant's own maintenance requests — `work_orders_tenant_select` scopes this
 * to the unit(s) they lease, same unfiltered-`useList` shape `PortalDocuments`
 * already uses: RLS decided the answer, this just renders it.
 */
export function MaintenanceList() {
  const navigate = useNavigate();

  const { data, isLoading, error } = useList<MaintenanceRequestRow>({
    resource: 'work-orders',
    pagination: { mode: 'off' },
  });

  const columns: Column<MaintenanceRequestRow>[] = [
    {
      key: 'title',
      header: 'Request',
      render: (row) => (
        <>
          <strong>{row.title}</strong>
          <div className="muted">
            {row.unitLabel}, {row.propertyName}
          </div>
        </>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <span className="pill">{humanizeEnum(row.status)}</span>,
    },
    {
      key: 'submitted',
      header: 'Submitted',
      render: (row) => (row.createdAt ? row.createdAt.slice(0, 10) : '—'),
    },
  ];

  return (
    <section className="stack">
      <div className="row">
        <h1>Maintenance</h1>
        <span className="shell__spacer" />
        <button
          type="button"
          className="button"
          onClick={() => void navigate('/portal/tenant/maintenance/new')}
        >
          New request
        </button>
      </div>

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        error={error}
        onRowClick={(row) => void navigate(`/portal/tenant/maintenance/${row.id}`)}
        emptyMessage="You haven't submitted any maintenance requests yet."
      />
    </section>
  );
}
